/* ============================================================
   Dathent — Edit Strategy
   Opens from Content Plan ("Edit strategy" button).
   Ported from design pack pages/edit-strategy.jsx → ES module
   with real API wiring (no demo data).
   ============================================================ */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import { useActivity } from '../components/AppShell';
import { useGenerationStatus, OPERATION_KINDS } from '../state/GenerationStatusContext.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import CreationAccessNotice, { creationAccessState } from '../components/billing/CreationAccessNotice.jsx';

/* ---------------- Option lists ---------------- */
const ES_OFFER_OPTIONS = [
  { v: 'one',    label: 'A single product or collection', sub: 'e.g. winter dinnerware line' },
  { v: 'rec',    label: 'Recurring service or subscription', sub: 'e.g. monthly maintenance plan' },
  { v: 'awar',   label: 'Awareness / portfolio', sub: 'no specific offer — just brand presence' },
  { v: 'launch', label: 'A specific launch with a date', sub: 'e.g. event, drop, opening' },
];

// Tone options — keys MATCH the backend's `toneOfVoice` enum
// (onboarding-profile.ts normalizeTone) so the strategy edit screen
// shows the same choice the user picked during onboarding.
const ES_TONE_OPTIONS = [
  { v: 'friendly',      label: 'Friendly & warm',       sub: 'like a thoughtful friend' },
  { v: 'professional',  label: 'Professional & precise', sub: 'deep authority on craft' },
  { v: 'playful',       label: 'Playful & witty',       sub: 'a smile in every post' },
  { v: 'authoritative', label: 'Authoritative & bold',  sub: 'strong opinions, sharp claims' },
  { v: 'inspirational', label: 'Inspirational',         sub: 'story-driven, uplifting' },
  { v: 'educational',   label: 'Educational',           sub: 'clear, useful, instructive' },
  { v: 'custom',        label: 'Custom',                sub: 'a tone uniquely yours' },
];

// Visual style — keys match onboarding's VISUAL_STYLE_PRESETS so
// whatever the user picked in the picker shows here selected. Color
// palettes are kept for the swatch preview.
const ES_PALETTES = [
  { v: 'premium_dark', label: 'Premium dark',  sub: 'Elegant, restrained, luxe',        palette: ['#0F1419', '#3A2E58', '#766858', '#D9CBB3'] },
  { v: 'minimalist',   label: 'Minimalist',    sub: 'Clean, calm, lots of white space', palette: ['#FAFAFA', '#E5E5E5', '#9CA3AF', '#111111'] },
  { v: 'modern',       label: 'Modern glossy', sub: 'Sleek, contemporary, sharp',       palette: ['#5B7EFF', '#A555F5', '#0E0E10', '#F4F4F4'] },
  { v: 'vibrant',      label: 'Vibrant',       sub: 'Bold colors, high energy',         palette: ['#FF6F61', '#FFD66B', '#E455CF', '#FFFFFF'] },
  { v: 'playful',      label: 'Playful',       sub: 'Friendly, quirky, warm',           palette: ['#FFB36B', '#FF7AA9', '#FFE89A', '#23222B'] },
  { v: 'corporate',    label: 'Corporate',     sub: 'Formal, trustworthy, structured',  palette: ['#0E5BC1', '#1E40AF', '#FFFFFF', '#1F2128'] },
  { v: 'vintage',      label: 'Vintage',       sub: 'Nostalgic, textured, classic',     palette: ['#8C6E4A', '#C99966', '#F3EBDD', '#3B2A1B'] },
];

const ES_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'uk', name: 'Українська' },
  { code: 'ru', name: 'Русский' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'pl', name: 'Polski' },
  { code: 'cs', name: 'Čeština' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'sv', name: 'Svenska' },
  { code: 'lt', name: 'Lietuvių' },
  { code: 'lv', name: 'Latviešu' },
];

const ES_PLATFORMS = [
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook',  name: 'Facebook' },
  { id: 'linkedin',  name: 'LinkedIn' },
  { id: 'x',         name: 'X' },
  { id: 'tiktok',    name: 'TikTok' },
  { id: 'telegram',  name: 'Telegram' },
  { id: 'threads',   name: 'Threads' },
];

// i18n key maps for the option lists above. The arrays keep their stable
// `v` enum values (used by the save/parse logic); these tables only map
// each value to the translation key for its user-facing label/sub copy.
const ES_OFFER_I18N = {
  one:    { label: 'editStrategy.offerOneLabel',    sub: 'editStrategy.offerOneSub' },
  rec:    { label: 'editStrategy.offerRecLabel',    sub: 'editStrategy.offerRecSub' },
  awar:   { label: 'editStrategy.offerAwarLabel',   sub: 'editStrategy.offerAwarSub' },
  launch: { label: 'editStrategy.offerLaunchLabel', sub: 'editStrategy.offerLaunchSub' },
};
const ES_TONE_I18N = {
  friendly:      { label: 'editStrategy.toneFriendlyLabel',      sub: 'editStrategy.toneFriendlySub' },
  professional:  { label: 'editStrategy.toneProfessionalLabel',  sub: 'editStrategy.toneProfessionalSub' },
  playful:       { label: 'editStrategy.tonePlayfulLabel',       sub: 'editStrategy.tonePlayfulSub' },
  authoritative: { label: 'editStrategy.toneAuthoritativeLabel', sub: 'editStrategy.toneAuthoritativeSub' },
  inspirational: { label: 'editStrategy.toneInspirationalLabel', sub: 'editStrategy.toneInspirationalSub' },
  educational:   { label: 'editStrategy.toneEducationalLabel',   sub: 'editStrategy.toneEducationalSub' },
  custom:        { label: 'editStrategy.toneCustomLabel',        sub: 'editStrategy.toneCustomSub' },
};
const ES_PALETTE_I18N = {
  premium_dark: 'editStrategy.palettePremiumDarkLabel',
  minimalist:   'editStrategy.paletteMinimalistLabel',
  modern:       'editStrategy.paletteModernLabel',
  vibrant:      'editStrategy.paletteVibrantLabel',
  playful:      'editStrategy.palettePlayfulLabel',
  corporate:    'editStrategy.paletteCorporateLabel',
  vintage:      'editStrategy.paletteVintageLabel',
};

/* ---------------- Helpers ---------------- */
const PERIOD_TO_WEEKS = { week: 1, month: 4.3, quarter: 13 };

function postsPerPeriodToPerWeek(posts, period) {
  const div = PERIOD_TO_WEEKS[period] || 4.3;
  return Math.max(1, Math.min(21, Math.round((posts || 0) / div)));
}

function perWeekToPostsPeriod(perWeek) {
  // posts_per_week is the canonical stored unit (the autopilot generates this
  // many each weekly cycle), so load it back as a WHOLE number in the per-week
  // view — exact round-trip, no fractional drift and no silent period swap. The
  // user can switch to month/quarter to set a volume in those units; it
  // re-resolves to whole posts/week on save.
  const n = Math.max(1, Math.min(21, Math.round(perWeek || 3)));
  return { posts: n, period: 'week' };
}

function parseTones(brand) {
  // toneOfVoice is single string; customToneDescription may carry the rest as CSV.
  const set = new Set();
  if (brand?.toneOfVoice) set.add(String(brand.toneOfVoice).toLowerCase());
  const custom = brand?.customToneDescription;
  if (typeof custom === 'string' && custom.includes(',')) {
    custom.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean).forEach((t) => set.add(t));
  }
  // Filter to known options only — tone keys now match backend exactly.
  const known = new Set(ES_TONE_OPTIONS.map((o) => o.v));
  const list = Array.from(set).filter((t) => known.has(t)).slice(0, 3);
  return list.length ? list : ['friendly'];
}

function parseLanguages(brand) {
  const primary = brand?.language || 'en';
  const list = [primary];
  // sourceMetadata may carry additional languages from onboarding
  const meta = brand?.sourceMetadata;
  if (meta && Array.isArray(meta.languages)) {
    meta.languages.forEach((l) => {
      if (l && !list.includes(l)) list.push(l);
    });
  }
  return list.filter((l) => ES_LANGUAGES.some((opt) => opt.code === l));
}

function parsePalette(brand) {
  // visualStyle now uses the same canonical keys as onboarding, so a
  // direct match should be the common path. Soft fallbacks handle a
  // few legacy values that lived under the old EditStrategy palette
  // names (natural/editorial/studio/etc.).
  const style = String(brand?.visualStyle || '').toLowerCase();
  const direct = ES_PALETTES.find((p) => style === p.v);
  if (direct) return direct.v;
  // Legacy → canonical re-mapping (pre-unification users may have
  // these strings stored in their brand_profile).
  if (style === 'natural')   return 'vintage';
  if (style === 'editorial') return 'minimalist';
  if (style === 'studio')    return 'minimalist';
  if (style === 'electric')  return 'modern';
  if (style === 'matte')     return 'premium_dark';
  if (style === 'sun')       return 'playful';
  // Loose substring fallbacks for AI free-text styles ("editorial dark", etc.)
  if (style.includes('premium') || style.includes('luxe') || style.includes('elegant')) return 'premium_dark';
  if (style.includes('mini') || style.includes('clean'))    return 'minimalist';
  if (style.includes('modern') || style.includes('glossy')) return 'modern';
  if (style.includes('vibrant') || style.includes('bold'))  return 'vibrant';
  if (style.includes('playful') || style.includes('quirk')) return 'playful';
  if (style.includes('corp') || style.includes('formal'))   return 'corporate';
  if (style.includes('vintage') || style.includes('retro')) return 'vintage';
  return 'premium_dark';
}

// Multi-select visual styles. Reads the brand's `visualStyles` set, normalizing
// each key through the same canonical mapping as parsePalette, with the single
// `visualStyle` (primary) guaranteed FIRST. Falls back to [primary] for legacy
// single-style profiles, so existing brands show exactly one selected style.
function parsePalettes(brand) {
  const primary = parsePalette(brand);
  const raw = Array.isArray(brand?.visualStyles) ? brand.visualStyles : [];
  const mapped = raw.map((s) => parsePalette({ visualStyle: s })).filter(Boolean);
  return [...new Set([primary, ...mapped])];
}

function parseOffer(brand) {
  const offer = String(brand?.offer || '').toLowerCase();
  if (ES_OFFER_OPTIONS.some((o) => o.v === offer)) return offer;
  if (offer.includes('launch')) return 'launch';
  if (offer.includes('subscription') || offer.includes('recurring')) return 'rec';
  if (offer.includes('portfolio') || offer.includes('awareness')) return 'awar';
  return 'one';
}

// Backend stores goals as snake_case slugs ("attract_new_clients") to keep
// the AI prompts simple. Users shouldn't see those raw — humanize for the
// textarea. Round-trip is safe: backend re-normalizes on save.
function humanizeGoal(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  // Already looks human? Leave it.
  if (/[A-Z\s]/.test(s) && !s.includes('_')) return s;
  return s
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function buildInitial(brand, prefs, accounts) {
  const connectedSet = new Set(
    (accounts || [])
      .map((a) => String(a?.platform || '').toLowerCase())
      .filter(Boolean)
  );
  const preferred = Array.isArray(brand?.preferredPlatforms) && brand.preferredPlatforms.length
    ? brand.preferredPlatforms
    : Array.from(connectedSet);

  return {
    // Brand identity — the most foundational pair of fields. `name`
    // anchors every prompt and every UI surface; `website` is the
    // canonical link the AI uses for CTAs (when allowed by platform)
    // and the link clients click from the Content Plan summary. When
    // a brand has no real site (e.g. social-only businesses) the
    // user can paste their Instagram/Telegram profile URL here and
    // it'll be treated the same way.
    name: brand?.businessName || '',
    website: brand?.websiteUrl || '',
    business: brand?.businessDescription || '',
    offer: parseOffer(brand),
    audience: brand?.targetAudience || '',
    goal: humanizeGoal(Array.isArray(brand?.goals) && brand.goals.length ? brand.goals[0] : ''),
    positioning: brand?.positioning || '',
    tones: parseTones(brand),
    // Free-form "How your brand sounds" description. Stored verbatim
    // in brand_profile.custom_tone_description. The Voice Instruction
    // block in the AI prompt elevates this field to the top of the
    // system message — so whatever the user writes here is exactly
    // what the model is told to mimic.
    // Pre-populated from existing customToneDescription. Older brand
    // profiles may have a CSV of tone keywords here (legacy multi-tone
    // storage abuse, now deprecated); the user can rewrite that into a
    // proper sentence, clear it, or leave it.
    brandVoice: brand?.customToneDescription || '',
    palette: parsePalette(brand),
    palettes: parsePalettes(brand),
    brandColors: Array.isArray(brand?.brandColors) ? brand.brandColors.filter(Boolean) : [],
    applyBrandColors: brand?.applyBrandColors !== false,
    languages: parseLanguages(brand),
    cadence: perWeekToPostsPeriod(prefs?.postsPerWeek),
    channels: preferred.filter((p) => ES_PLATFORMS.some((opt) => opt.id === p)),
  };
}

function unpackResponse(res, key) {
  return res?.data?.[key] || res?.[key] || res?.data || res || null;
}

function errMessage(err) {
  if (!err) return 'Unknown error';
  return err?.body?.error?.message || err?.body?.message || err?.message || 'Unknown error';
}

/* ---------------- Scroll-spy ---------------- */
function useScrollSpy(ids, offset = 140) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const onScroll = () => {
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top < offset) {
          current = id;
        }
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [ids.join(','), offset]);
  return active;
}

/* ---------------- Section + Field ---------------- */
// Sections whose fields are STRATEGY-RELEVANT (feed plan regeneration) and are
// therefore frozen on the free plan after onboarding — identity (name/website),
// business (description/offer), audience, goal+positioning. The voice / visual /
// language / channels sections stay editable on free because they apply at
// post-write time without a plan rebuild (see STRATEGY_RELEVANT_FIELDS).
const LOCKED_SECTION_IDS = ['identity', 'business', 'audience', 'goal', 'cadence'];

// MATERIAL change = brand identity (name / website). On paid plans, editing
// these re-runs market research (the brand the report is about actually
// changed) and then rebuilds the plan on the fresh foundation. The other
// strategy-relevant fields (business/audience/offer/goal/positioning) only need
// a plan rebuild from the new profile — no new research — so they keep the
// existing synchronous regenerate. (Free locks all of them — see Tail 2.)
const MATERIAL_FIELDS = ['name', 'website'];

// Build the research intake from the edited form + the raw saved profile.
// Mirrors ResearchTab.buildIntake — crucially it carries geography (from the
// profile timezone) so the geo-driven query planner researches the right market
// for the NEW brand. A brand-change run MUST pass a fresh intake; an empty one
// would inherit the previous report's snapshot and re-research the OLD brand.
function buildBrandChangeIntake(data, rawBrand) {
  const description = String(data.business || '').trim();
  const querySeed = description.split(/[.!?\n]/)[0].trim().slice(0, 120) || data.name || '';
  const tz = String(rawBrand?.timezone || '');
  const tzCity = tz.includes('/') ? tz.split('/').pop().replace(/_/g, ' ') : '';
  const intake = {
    business: querySeed || undefined,
    brandName: (data.name || '').trim() || undefined,
    businessDescription: description || undefined,
    targetAudience: (data.audience || '').trim() || undefined,
    offer: data.offer || undefined,
    positioning: (data.positioning || '').trim() || undefined,
    website: (data.website || '').trim() || undefined,
    geography: tzCity || rawBrand?.geography || undefined,
    timezone: rawBrand?.timezone || undefined,
    language: (data.languages && data.languages[0]) || undefined,
  };
  return Object.fromEntries(Object.entries(intake).filter(([, value]) => value !== undefined));
}

function Section({ id, eyebrow, title, sub, children, locked, lockedLabel }) {
  return (
    <section id={id} className={`es-section card ${locked ? 'is-locked' : ''}`}>
      <header className="es-section-h">
        <div className="es-section-eyerow">
          <div className="eyebrow"><span>{eyebrow}</span></div>
          {locked && (
            <span className="es-sec-lock" title={lockedLabel}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>
              {lockedLabel}
            </span>
          )}
        </div>
        <h2>{title}</h2>
        {sub && <p>{sub}</p>}
      </header>
      <div className="es-section-body">{children}</div>
    </section>
  );
}

function Field({ label, hint, count, max, children, full }) {
  return (
    <div className={`es-field ${full ? 'is-full' : ''}`}>
      <div className="es-field-h">
        {label && <label className="es-field-l">{label}</label>}
        {(count != null && max != null) && (
          <span className={`es-field-count mono ${count > max ? 'is-over' : count > max * 0.85 ? 'is-warn' : ''}`}>
            {count} / {max}
          </span>
        )}
      </div>
      {children}
      {hint && <div className="es-field-hint">{hint}</div>}
    </div>
  );
}

/* ---------------- Top-level EditStrategy ---------------- */
export const EditStrategy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setActivity } = useActivity();
  // Free plan is FROZEN after onboarding: the brand identity (name + website)
  // is locked because changing it would re-run market research + rebuild the
  // plan — a paid capability. The dashboard provider loads lazily, so nudge it.
  const dash = useDashboardData() || {};
  const currentEntitlements = dash.entitlements;
  const creationAllowed = creationAccessState(dash) === 'allowed';
  // TASK-327 (D-B): this used to read `features.research !== true`, which is
  // a RESEARCH-ENTITLEMENT check, not a billing one — Starter's features.research
  // is false by design (its research stays a 1x/month teaser, not the Growth+
  // full report), so a paying, active Starter org was locked out of editing
  // its own strategy and banner-shamed with "locked on the free plan".
  // `strategy_editing` is a dedicated entitlement flag (true for every paid
  // tier including Starter, false for free/posting) that decouples "can edit
  // the strategy" from "gets the automatic deep-research report". Fail-soft:
  // when an old backend hasn't shipped the flag yet (undefined), fall back to
  // the previous `features.research === true` reading so behaviour is
  // unchanged until the entitlements package rolls out (TASK-327-BE).
  const strategyEditingFlag = currentEntitlements?.features?.strategy_editing;
  const canEditStrategy = strategyEditingFlag ?? (currentEntitlements?.features?.research === true);
  const strategyLocked = currentEntitlements != null && canEditStrategy !== true;
  // Editing is forbidden while a regeneration is rebuilding the whole strategy —
  // any edits would be silently overwritten. If the user slipped in here during
  // the brief window before the button locked, send them back to watch the
  // rebuild (the op is server-tracked, so this guard survives reloads too).
  const { visibleOperations } = useGenerationStatus();
  const regenActive = (visibleOperations || []).some(
    (o) => o.kind === OPERATION_KINDS.PLAN_REGENERATE && o.status === 'running'
  );
  useEffect(() => {
    if (regenActive) navigate('/app/plan', { replace: true });
  }, [regenActive, navigate]);
  const [initialData, setInitialData] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [colorDraft, setColorDraft] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef(null);
  // Phase 2 — paid brand-change cascade. brandRawRef keeps the raw saved profile
  // so the research intake can carry geography; the confirm dialog gates the
  // material change (re-runs research + rebuilds plan) and shows remaining
  // research updates loaded from entitlements.
  const brandRawRef = useRef(null);
  const [confirmBrandOpen, setConfirmBrandOpen] = useState(false);
  const [researchUsage, setResearchUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  useEffect(() => {
    if (!addOpen) return undefined;
    const onDoc = (e) => { if (!addRef.current?.contains(e.target)) setAddOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [addOpen]);
  // Deep-link: New Post's "Change in Edit Strategy" sends #bc-colours, and the
  // TASK-327 dashboard/Content Plan CTAs send #business (and #audience) — once
  // the form has loaded, scroll the named block into view. The hash allowlist
  // used to be its own hardcoded copy of the section-nav ids (drift risk); it
  // now reuses the scroll-spy's own `sectionIds` + `goTo()` (defined below,
  // already initialized by the time this effect actually runs — React commits
  // effects after the full render body executes) with `bc-colours` as the one
  // extra anchor that isn't a top-level nav section.
  const hashScrolled = useRef(false);
  useEffect(() => {
    if (loading || hashScrolled.current) return;
    hashScrolled.current = true;
    const hash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
    if (!hash || ![...sectionIds, 'bc-colours'].includes(hash)) return;
    setTimeout(() => goTo(hash), 120);
  }, [loading]);

  // Load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      api.profile.getBrandProfile(),
      api.profile.getPublishingPreferences(),
      api.socialAccounts.list(),
    ]).then(([bRes, pRes, aRes]) => {
      if (cancelled) return;
      const brand = bRes.status === 'fulfilled' ? unpackResponse(bRes.value, 'brandProfile') : null;
      const prefs = pRes.status === 'fulfilled' ? unpackResponse(pRes.value, 'publishingPreferences') : null;
      const accs = aRes.status === 'fulfilled'
        ? (aRes.value?.accounts || aRes.value?.data?.accounts || aRes.value?.data || [])
        : [];
      setAccounts(Array.isArray(accs) ? accs : []);
      brandRawRef.current = brand || null;
      const built = buildInitial(brand, prefs, accs);
      setInitialData(built);
      setData(built);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const update = (key) => (value) => setData((d) => ({ ...d, [key]: value }));
  const toggleIn = (key, value) => {
    setData((d) => {
      const arr = d[key] || [];
      const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
      return { ...d, [key]: next };
    });
  };
  // Visual styles are multi-select with a designated PRIMARY (index 0 of the
  // set). Toggling keeps at least one selected; `palette` mirrors the primary
  // so the rest of the save path is unchanged.
  const toggleVisual = (v) => {
    setData((d) => {
      const cur = d.palettes || [];
      let next;
      if (cur.includes(v)) {
        if (cur.length === 1) return d; // always keep one style selected
        next = cur.filter((x) => x !== v);
      } else {
        next = [...cur, v];
      }
      return { ...d, palettes: next, palette: next[0] };
    });
  };
  const setPrimaryVisual = (v) => {
    setData((d) => {
      const cur = d.palettes || [];
      if (!cur.includes(v)) return d;
      const next = [v, ...cur.filter((x) => x !== v)];
      return { ...d, palettes: next, palette: v };
    });
  };
  // Brand colors editor. Normalizes to #rrggbb, dedupes, caps at 6 (the gate
  // only reads the first 6). Empty/invalid input is silently ignored.
  const normalizeHex = (raw) => {
    let h = String(raw || '').trim();
    if (!h) return null;
    if (h[0] !== '#') h = `#${h}`;
    if (/^#[0-9a-fA-F]{3}$/.test(h)) h = `#${h.slice(1).split('').map((c) => c + c).join('')}`;
    return /^#[0-9a-fA-F]{6}$/.test(h) ? h.toLowerCase() : null;
  };
  const addColor = () => {
    const hex = normalizeHex(colorDraft);
    if (!hex) { setColorDraft(''); return; }
    setData((d) => {
      const cur = d.brandColors || [];
      // Full (6) or duplicate → no-op. At the cap the user removes one first.
      if (cur.length >= 6 || cur.map((c) => String(c).toLowerCase()).includes(hex)) return d;
      return { ...d, brandColors: [...cur, hex] };
    });
    setColorDraft('');
    setAddOpen(false);
  };
  const removeColor = (idx) =>
    setData((d) => ({ ...d, brandColors: (d.brandColors || []).filter((_, i) => i !== idx) }));

  const isDirty = useMemo(() => {
    if (!initialData || !data) return false;
    return JSON.stringify(data) !== JSON.stringify(initialData);
  }, [initialData, data]);

  // Scroll-spy nav
  const sectionIds = useMemo(
    () => ['identity', 'business', 'audience', 'goal', 'voice', 'visual', 'language', 'cadence', 'channels'],
    []
  );
  const active = useScrollSpy(sectionIds, 140);
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const handleDiscard = () => setData(initialData);

  // Fields whose change should trigger a paid plan regeneration.
  //
  // Narrowed down from the original 8-field list — only foundation-level
  // brand definition fields warrant re-running the strategy AI (and
  // spending credits). The dropped fields all affect content at write
  // time without needing a fresh strategy snapshot:
  //
  //   - tones / brandVoice — go straight into the Voice Instruction
  //     block on every post generation via the brand_profile lookup.
  //     Adding a new tone or rewriting voice prose doesn't shift
  //     pillars or content angles, so no re-slot needed.
  //   - channels — just changes WHICH accounts the user posts to;
  //     doesn't change WHAT gets written.
  //   - cadence — just changes posting frequency; pillars unchanged.
  //   - palette / language — UI/visual choices, brand profile only.
  //
  // Foundation fields below DO require regen because pillars / angles /
  // platform instructions are derived from them by the strategy AI:
  //   name + website     — brand identity. A renamed brand or new site
  //                        changes how every post is framed (canonical
  //                        link in CTAs, brand mentions in copy).
  //   business / offer   — what the brand actually does and sells.
  //   audience           — who the posts target.
  //   goal               — what the posts are trying to move.
  //   positioning        — competitive frame.
  const STRATEGY_RELEVANT_FIELDS = ['name', 'website', 'business', 'audience', 'offer', 'goal', 'positioning'];

  const strategyRelevantChanged = () => {
    if (!initialData) return true; // first save: regenerate
    for (const key of STRATEGY_RELEVANT_FIELDS) {
      const a = JSON.stringify(initialData[key] ?? null);
      const b = JSON.stringify(data[key] ?? null);
      if (a !== b) return true;
    }
    return false;
  };

  const materialChanged = () => {
    if (!initialData) return false;
    for (const key of MATERIAL_FIELDS) {
      if (JSON.stringify(initialData[key] ?? null) !== JSON.stringify(data[key] ?? null)) return true;
    }
    return false;
  };

  // Lazily load the research-update allowance for the brand-change confirm
  // dialog (usage.research_runs_monthly = {used, limit, remaining}).
  const loadResearchUsage = async () => {
    setUsageLoading(true);
    try {
      const res = await api.billing.getEntitlements();
      setResearchUsage((res?.data ?? res)?.usage?.research_runs_monthly ?? null);
    } catch { setResearchUsage(null); }
    finally { setUsageLoading(false); }
  };

  // Save button entry point. On a paid plan a MATERIAL change (name/website) is
  // consequential + metered — it re-runs market research and rebuilds the plan —
  // so it goes through a confirm dialog first. Everything else saves straight
  // away (free can't change any strategy field; non-material paid edits just
  // re-slot the plan synchronously below).
  const onSaveClick = () => {
    if (!data || saving || !creationAllowed) return;
    if (!strategyLocked && materialChanged()) {
      setResearchUsage(null);
      loadResearchUsage();
      setConfirmBrandOpen(true);
      return;
    }
    handleSave(false);
  };

  const handleSave = async (brandChange = false) => {
    if (!data || saving || !creationAllowed) return;
    setConfirmBrandOpen(false);
    setSaving(true);
    setError(null);
    const needsRegeneration = strategyRelevantChanged();
    // Free is frozen after onboarding (content_plan_regenerations_monthly = 0):
    // saving still stores the brand profile, but we DON'T call the paid plan
    // regeneration (it would 402). Paid plans regenerate as before. A paid
    // brandChange takes a different path entirely: re-run research, then a FREE
    // bundled plan rebuild lands once it's ready (research-worker cascade).
    const willRegen = !brandChange && needsRegeneration && !strategyLocked;
    setActivity({
      kind: 'planning',
      project: brandChange
        ? t('editStrategy.activityRerunningResearch')
        : willRegen ? t('editStrategy.activityUpdatingStrategy') : t('editStrategy.activitySavingBrand'),
      step: brandChange
        ? t('editStrategy.activityResearchStep')
        // Plain-language step text. The previous phrasing
        // ("Rebalancing pillars and re-slotting posts") was internal
        // jargon — particularly confusing now that the Pillars block
        // is no longer surfaced in /app/plan, so users have no UI
        // referent for "pillars". The user just sees "Updating content
        // plan · <step>" and needs to know what's actually happening:
        // their changes are being applied to the plan.
        : willRegen ? t('editStrategy.activityApplyingChanges') : t('editStrategy.activityUpdatingProfile'),
    });

    try {
      // 1. Brand profile
      const tones = (data.tones || []).slice(0, 3);
      const languages = (data.languages || []).slice();
      // brandVoice (free-form prose) is the primary source for the
      // Voice Instruction block in AI prompts. When the user has
      // written something there, save it verbatim to
      // customToneDescription. When empty, fall back to joining tone
      // keywords into a CSV — preserves the legacy behaviour so older
      // brand profiles without an explicit voice description still
      // benefit from multi-tone hints.
      const brandVoiceText = (data.brandVoice || '').trim();
      const customToneDescription = brandVoiceText
        ? brandVoiceText
        : tones.length > 1
          ? tones.join(', ')
          : null;
      const brandPayload = {
        businessName: (data.name || '').trim() || null,
        websiteUrl: (data.website || '').trim() || null,
        businessDescription: (data.business || '').trim(),
        targetAudience: (data.audience || '').trim(),
        offer: data.offer || 'one',
        goals: data.goal ? [data.goal.trim()] : ['visibility'],
        positioning: (data.positioning || '').trim() || null,
        toneOfVoice: tones[0] || 'professional',
        customToneDescription,
        visualStyle: (data.palettes && data.palettes[0]) || data.palette || 'premium_dark',
        visualStyles: (data.palettes || []).slice(),
        brandColors: (data.brandColors || []).slice(),
        applyBrandColors: data.applyBrandColors !== false,
        language: languages[0] || 'en',
        preferredPlatforms: (data.channels || []).slice(),
        sourceMetadata: { languages },
      };

      // 2. Publishing preferences (cadence). Free-plan cadence is frozen, so
      // never send a publishing-preferences write for a locked user.
      const updates = [api.profile.updateBrandProfile(brandPayload)];
      if (!strategyLocked) {
        const ppw = postsPerPeriodToPerWeek(data.cadence?.posts, data.cadence?.period);
        updates.push(api.profile.updatePublishingPreferences({ postsPerWeek: ppw }));
      }
      await Promise.all(updates);

      // 3a. Material change (name/website) → re-run market research on the NEW
      // brand. The gateway meters this as ONE research update; the plan rebuild
      // rides along free once the research is ready (research-worker
      // 'brand_change' cascade → durable rebuild + plan_rebuilt bell). Send a
      // fresh intake so the run researches the new brand, not the old snapshot.
      if (brandChange) {
        await api.research.create({
          trigger: 'brand_change',
          language: (data.languages && data.languages[0]) || 'en',
          intake: buildBrandChangeIntake(data, brandRawRef.current),
        });
        api.invalidateCache?.('/api/research');
        setInitialData(data);
        setActivity({ kind: 'done', message: t('editStrategy.activityResearchStarted') });
        navigate('/app/research');
        return;
      }

      // 3b. Recalculate strategy ONLY when a strategy-relevant field changed.
      // Pure language / visual style edits don't re-slot the calendar, so we
      // skip the paid generation call — saves credits, removes the "requires
      // confirmation before spending credits" wall on no-op saves.
      if (willRegen) {
        await api.strategy.generate({ confirmCreditSpend: true });
      }

      // Reset baseline to saved data
      setInitialData(data);
      setActivity({
        kind: 'done',
        message: willRegen
          ? t('editStrategy.activityStrategySaved')
          : t('editStrategy.activityBrandSaved'),
      });
    } catch (err) {
      // The brand profile is already saved; only the research/regen call failed.
      // Map the two expected quota/lock cases to clear copy.
      // TASK-331: PLAN_LAPSED first — a lapsed org's regen call 402s with that
      // code, and "upgrade to raise your quota" copy is the wrong message for it.
      if (brandChange && err?.code === 'PLAN_LAPSED') setError(t('paymentIssue.lapsedBody'));
      else if (brandChange && err?.status === 402) setError(t('editStrategy.brandChangeQuota'));
      else if (brandChange && err?.status === 409) setError(t('editStrategy.brandChangeBusy'));
      else if (err?.status === 409) {
        // research_in_progress (a research run is active) vs plan_rebuild_in_progress
        // (a durable rebuild is running) — show copy that matches what's busy.
        setError(err?.body?.error?.reason === 'research_in_progress'
          ? t('editStrategy.brandChangeBusy')
          : t('editStrategy.rebuildBusy'));
      } else setError(errMessage(err));
      setActivity(null);
    } finally {
      setSaving(false);
    }
  };

  const connectedSet = useMemo(() => {
    const set = new Set();
    accounts.forEach((a) => {
      const p = String(a?.platform || '').toLowerCase();
      if (p) set.add(p);
    });
    return set;
  }, [accounts]);

  // Char limits
  const businessMax = 400;
  const audienceMax = 400;
  const goalMax = 240;
  const positioningMax = 200;

  if (loading || !data) {
    return (
      <div className="es-root">
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          {t('editStrategy.loading')}
        </div>
      </div>
    );
  }

  const publishLimit = Number(currentEntitlements?.usage?.published_posts_monthly?.limit);
  const hasPublishLimit = currentEntitlements?.usage?.published_posts_monthly?.limit != null
    && Number.isFinite(publishLimit);
  const planName = currentEntitlements?.planName || currentEntitlements?.plan;
  const selectedMonthlyPosts = data.cadence?.period === 'week'
    ? (data.cadence?.posts || 0) * 4.3
    : data.cadence?.period === 'quarter'
      ? (data.cadence?.posts || 0) / 3
      : (data.cadence?.posts || 0);
  const publishLimitExceeded = hasPublishLimit && selectedMonthlyPosts > publishLimit;

  return (
    <div className="es-root">
      {/* Top */}
      <div className="es-top">
        <div>
          <button
            className="es-back"
            type="button"
            onClick={() => navigate('/app/plan')}
          >
            <I.chevLeft size={14} /> {t('editStrategy.backToContentPlan')}
          </button>
          <h1 className="es-h">{t('editStrategy.heading')}</h1>
          <p className="es-sub">
            {t('editStrategy.subheading')}
          </p>
        </div>
        {isDirty && (
          <span className="pill pill-warning" style={{ alignSelf: 'center' }}>
            <span className="dot" />{t('editStrategy.unsavedChanges')}
          </span>
        )}
      </div>

      {/* Free plan: the strategy foundation (identity + business + audience +
          goal) and cadence are frozen to the onboarding answers — changing it re-runs
          market research and rebuilds the content plan, a paid capability.
          The write-time fields below (voice, visuals, language and channels)
          stay fully editable because they apply to future posts
          without a plan rebuild. One cohesive banner explains the whole
          freeze; each frozen section also carries a small lock badge. */}
      {strategyLocked && (
        <div className="es-freeze">
          <span className="es-freeze-ic" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>
          </span>
          <div className="es-freeze-tx">
            <div className="es-freeze-t">{t('editStrategy.freezeTitle')}</div>
            <p className="es-freeze-b">
              <Trans
                i18nKey="editStrategy.freezeBody"
                components={{
                  plans: <button type="button" className="es-freeze-link" onClick={() => navigate('/app/plans')} />,
                }}
              />
            </p>
          </div>
          <button type="button" className="btn btn-primary btn-sm es-freeze-cta" onClick={() => navigate('/app/plans')}>
            {t('editStrategy.seePlans')}
          </button>
        </div>
      )}

      <div className="es-grid">
        {/* Left — section nav */}
        <aside className="es-nav">
          <div className="es-nav-h">
            <span className="eyebrow"><span>{t('editStrategy.navSections')}</span></span>
          </div>
          <nav>
            {[
              { id: 'identity', label: t('editStrategy.navIdentity'),    icon: I.user },
              { id: 'business', label: t('editStrategy.navBusiness'),    icon: I.layers },
              { id: 'audience', label: t('editStrategy.navAudience'),    icon: I.users },
              { id: 'goal',     label: t('editStrategy.navGoal'),        icon: I.target },
              { id: 'voice',    label: t('editStrategy.navVoice'),       icon: I.message },
              { id: 'visual',   label: t('editStrategy.navVisual'),      icon: I.image },
              { id: 'language', label: t('editStrategy.navLanguages'),   icon: I.globe },
              { id: 'cadence',  label: t('editStrategy.navCadence'),     icon: I.calendar },
              { id: 'channels', label: t('editStrategy.navChannels'),    icon: I.share },
            ].map((s) => {
              const locked = strategyLocked && LOCKED_SECTION_IDS.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`es-nav-link ${active === s.id ? 'is-active' : ''} ${locked ? 'is-locked' : ''}`}
                  onClick={() => goTo(s.id)}
                >
                  <s.icon size={14} />
                  <span>{s.label}</span>
                  {locked && (
                    <svg className="es-nav-lock" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Right — sections */}
        <div className="es-content">
          <CreationAccessNotice />
          {/* IDENTITY — brand name + canonical link.
              First section because it's the most foundational fact:
              every prompt mentions the brand by name, every CTA needs a
              canonical URL when the platform supports clickable links.
              Empty fields are valid (a fresh workspace can have no name
              filled yet) — downstream prompts and UI gracefully fall
              back to "your brand" / no link respectively. */}
          <Section
            id="identity"
            eyebrow={t('editStrategy.identityEyebrow')}
            title={t('editStrategy.identityTitle')}
            sub={t('editStrategy.identitySub')}
            locked={strategyLocked}
            lockedLabel={t('editStrategy.lockedBadge')}
          >
            <Field
              label={t('editStrategy.brandNameLabel')}
              hint={t('editStrategy.brandNameHint')}
              count={(data.name || '').length}
              max={80}
            >
              <input
                className="es-input"
                type="text"
                value={data.name || ''}
                onChange={(e) => update('name')(e.target.value)}
                placeholder={t('editStrategy.brandNamePlaceholder')}
                disabled={strategyLocked}
              />
            </Field>
            <Field
              label={t('editStrategy.websiteLabel')}
              hint={t('editStrategy.websiteHint')}
              count={(data.website || '').length}
              max={200}
            >
              <input
                className="es-input"
                type="url"
                value={data.website || ''}
                onChange={(e) => update('website')(e.target.value)}
                placeholder={t('editStrategy.websitePlaceholder')}
                inputMode="url"
                autoCapitalize="off"
                spellCheck={false}
                disabled={strategyLocked}
              />
            </Field>
          </Section>

          {/* BUSINESS */}
          <Section
            id="business"
            eyebrow={t('editStrategy.businessEyebrow')}
            title={t('editStrategy.businessTitle')}
            sub={t('editStrategy.businessSub')}
            locked={strategyLocked}
            lockedLabel={t('editStrategy.lockedBadge')}
          >
            <Field
              label={t('editStrategy.descriptionLabel')}
              count={(data.business || '').length}
              max={businessMax}
              hint={t('editStrategy.descriptionHint')}
              full
            >
              <textarea
                className="es-textarea"
                rows={3}
                value={data.business || ''}
                onChange={(e) => update('business')(e.target.value)}
                disabled={strategyLocked}
              />
            </Field>

            <Field label={t('editStrategy.offerLabel')} full>
              <div className="es-choice">
                {ES_OFFER_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    className={`es-choice-card ${data.offer === o.v ? 'is-active' : ''}`}
                    onClick={() => update('offer')(o.v)}
                    disabled={strategyLocked}
                  >
                    <span className="es-choice-radio">
                      {data.offer === o.v && <I.check size={11} />}
                    </span>
                    <div>
                      <div className="es-choice-label">{t(ES_OFFER_I18N[o.v].label)}</div>
                      <div className="es-choice-sub">{t(ES_OFFER_I18N[o.v].sub)}</div>
                    </div>
                  </button>
                ))}
              </div>
            </Field>
          </Section>

          {/* AUDIENCE */}
          <Section
            id="audience"
            eyebrow={t('editStrategy.audienceEyebrow')}
            title={t('editStrategy.audienceTitle')}
            sub={t('editStrategy.audienceSub')}
            locked={strategyLocked}
            lockedLabel={t('editStrategy.lockedBadge')}
          >
            <Field label={t('editStrategy.audienceFieldLabel')} count={(data.audience || '').length} max={audienceMax} full>
              <textarea
                className="es-textarea"
                rows={3}
                value={data.audience || ''}
                onChange={(e) => update('audience')(e.target.value)}
                disabled={strategyLocked}
              />
            </Field>
          </Section>

          {/* GOAL & POSITIONING */}
          <Section
            id="goal"
            eyebrow={t('editStrategy.goalEyebrow')}
            title={t('editStrategy.goalTitle')}
            sub={t('editStrategy.goalSub')}
            locked={strategyLocked}
            lockedLabel={t('editStrategy.lockedBadge')}
          >
            <Field
              label={t('editStrategy.goalFieldLabel')}
              hint={t('editStrategy.goalFieldHint')}
              count={(data.goal || '').length}
              max={goalMax}
              full
            >
              <textarea
                className="es-textarea"
                rows={2}
                value={data.goal || ''}
                onChange={(e) => update('goal')(e.target.value)}
                disabled={strategyLocked}
              />
            </Field>
            <Field
              label={t('editStrategy.positioningLabel')}
              hint={t('editStrategy.positioningHint')}
              count={(data.positioning || '').length}
              max={positioningMax}
              full
            >
              <textarea
                className="es-textarea"
                rows={2}
                value={data.positioning || ''}
                onChange={(e) => update('positioning')(e.target.value)}
                disabled={strategyLocked}
              />
            </Field>
          </Section>

          {/* VOICE / TONE */}
          <Section
            id="voice"
            eyebrow={t('editStrategy.voiceEyebrow')}
            title={t('editStrategy.voiceTitle')}
            sub={t('editStrategy.voiceSub')}
          >
            <Field full>
              <div className="es-choice">
                {ES_TONE_OPTIONS.map((o) => {
                  const on = (data.tones || []).includes(o.v);
                  const disabled = !on && (data.tones || []).length >= 3;
                  return (
                    <button
                      key={o.v}
                      type="button"
                      className={`es-choice-card ${on ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
                      onClick={() => !disabled && toggleIn('tones', o.v)}
                    >
                      <span className="es-choice-check">{on && <I.check size={11} />}</span>
                      <div>
                        <div className="es-choice-label">{t(ES_TONE_I18N[o.v].label)}</div>
                        <div className="es-choice-sub">{t(ES_TONE_I18N[o.v].sub)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="es-meta-row">
                <span className="mono">{t('editStrategy.tonesSelected', { count: (data.tones || []).length })}</span>
              </div>
            </Field>

            {/* "How your brand sounds" — free-form voice description.
                Optional but high-leverage: when filled, this string is
                elevated to the TOP of the AI prompt as a literal Voice
                Instruction ("Write every word in this voice: '<text>'"),
                weighted higher than the tone preset chips above.
                Empty by default — tone presets above still drive voice
                via the preset expansion table on the worker. */}
            <Field
              label={t('editStrategy.brandVoiceLabel')}
              hint={t('editStrategy.brandVoiceHint')}
              count={(data.brandVoice || '').length}
              max={400}
              full
            >
              <textarea
                className="es-textarea"
                rows={3}
                value={data.brandVoice || ''}
                onChange={(e) => update('brandVoice')(e.target.value)}
                placeholder={t('editStrategy.brandVoicePlaceholder')}
              />
            </Field>
          </Section>

          {/* VISUAL */}
          <Section
            id="visual"
            eyebrow={t('editStrategy.visualEyebrow')}
            title={t('editStrategy.visualTitle')}
            sub={t('editStrategy.visualSub')}
          >
            <Field full hint={t('editStrategy.visualMultiHint')}>
              <div className="es-palette">
                {ES_PALETTES.map((p) => {
                  const on = (data.palettes || []).includes(p.v);
                  const isPrimary = on && (data.palettes || [])[0] === p.v;
                  return (
                    <button
                      key={p.v}
                      type="button"
                      className={`es-pal ${on ? 'is-active' : ''} ${isPrimary ? 'is-primary' : ''}`}
                      onClick={() => toggleVisual(p.v)}
                    >
                      <div className="es-pal-swatch">
                        <span style={{ background: p.palette[0] }} />
                        <span style={{ background: p.palette[1] }} />
                        <span style={{ background: p.palette[2] }} />
                        <span style={{ background: p.palette[3] }} />
                      </div>
                      <div className="es-pal-label">{t(ES_PALETTE_I18N[p.v])}</div>
                      {isPrimary && <span className="es-pal-primary-tag">★ {t('editStrategy.visualPrimaryTag')}</span>}
                      {on && (
                        <span className="es-pal-check">
                          <I.check size={11} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {(data.palettes || []).length > 1 && (
                <div className="es-pal-primary-row">
                  <label>{t('editStrategy.visualPrimaryLabel')}</label>
                  <select
                    className="es-pal-primary-select"
                    value={(data.palettes || [])[0] || ''}
                    onChange={(e) => setPrimaryVisual(e.target.value)}
                  >
                    {(data.palettes || []).map((v) => (
                      <option key={v} value={v}>{t(ES_PALETTE_I18N[v])}</option>
                    ))}
                  </select>
                </div>
              )}
            </Field>
            <Field full>
              <div id="bc-colours" className={`es-bc ${data.applyBrandColors !== false ? '' : 'is-off'}`}>
                <div className="es-bc-head">
                  <div className="es-bc-headl">
                    <span className="es-bc-t">
                      {t('editStrategy.colorsLabel')}
                      <span className="es-bc-ct">{(data.brandColors || []).length} / 6</span>
                    </span>
                    <span className="es-bc-s">{t('editStrategy.colorsHint')}</span>
                  </div>
                </div>
                <div className="es-bc-body">
                  <div className="es-chips" ref={addRef}>
                    {(data.brandColors || []).map((c, i) => (
                      <span key={`${c}-${i}`} className="es-cchip">
                        <span className="es-cchip-dot" style={{ background: c }} />
                        {String(c).toUpperCase()}
                        <button type="button" className="es-cchip-rm" onClick={() => removeColor(i)} aria-label={t('editStrategy.colorRemove')}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 6 18 18M18 6 6 18" /></svg>
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      className={`es-cadd ${(data.brandColors || []).length >= 6 ? 'is-full' : ''}`}
                      onClick={() => { if ((data.brandColors || []).length < 6) setAddOpen((o) => !o); }}
                      aria-disabled={(data.brandColors || []).length >= 6}
                    >
                      {(data.brandColors || []).length < 6 && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
                      )}
                      {(data.brandColors || []).length >= 6 ? t('editStrategy.colorsFull') : t('editStrategy.colorAdd')}
                    </button>
                    {addOpen && (data.brandColors || []).length < 6 && (
                      <div className="es-addpop">
                        <div className="es-addpop-row">
                          <input
                            className="es-addpop-native"
                            type="color"
                            value={normalizeHex(colorDraft) || '#7a5bff'}
                            onChange={(e) => setColorDraft(e.target.value.replace('#', ''))}
                            aria-label={t('editStrategy.colorPick')}
                          />
                          <span className="es-addpop-hex">
                            <span>#</span>
                            <input
                              value={colorDraft.replace('#', '')}
                              maxLength={6}
                              onChange={(e) => setColorDraft(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addColor(); } }}
                              placeholder="7A5BFF"
                              spellCheck={false}
                              autoCapitalize="off"
                              autoCorrect="off"
                            />
                          </span>
                        </div>
                        <button type="button" className="es-addpop-cta" onClick={addColor}>{t('editStrategy.colorAddCta')}</button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="es-bc-apply">
                  <div className="es-bc-applyl">
                    <span className="es-bc-ico">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="10.5" r="2.5" /><circle cx="8.5" cy="7.5" r="2.5" /><circle cx="6.5" cy="12.5" r="2.5" /><path d="M12 22a10 10 0 0 1 0-20 8 8 0 0 1 8 8c0 3-2 4-4 4h-2a2 2 0 0 0 0 4 2 2 0 0 1-1 4z" /></svg>
                    </span>
                    <span className="es-bc-applytx">
                      <span className="es-bc-applyt">{t('editStrategy.applyColorsLabel')}</span>
                      <span className="es-bc-applys">{t('editStrategy.applyColorsHint')}</span>
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={data.applyBrandColors !== false}
                    className={`cp-sw ${data.applyBrandColors !== false ? '' : 'is-off'}`}
                    onClick={() => update('applyBrandColors')(data.applyBrandColors === false)}
                  ><i /></button>
                </div>
              </div>
            </Field>
          </Section>

          {/* LANGUAGES — single global default. On Growth+ users can override
              per connected social account in Settings → Connected accounts. */}
          <Section
            id="language"
            eyebrow={t('editStrategy.languageEyebrow')}
            title={t('editStrategy.languageTitle')}
            sub={t('editStrategy.languageSub')}
          >
            <Field full>
              <div className="es-langs">
                {ES_LANGUAGES.map((l) => {
                  // Single-select: primary is data.languages[0] (data may still
                  // carry the legacy list for backward compat — we only honour
                  // the first entry now).
                  const active = (data.languages?.[0] || 'en') === l.code;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      className={`es-lang ${active ? 'is-active' : ''}`}
                      onClick={() => update('languages')([l.code])}
                    >
                      <span className="es-lang-code">{l.code.toUpperCase()}</span>
                      <span className="es-lang-name">{l.name}</span>
                      {active && <I.check size={12} className="es-lang-check" />}
                    </button>
                  );
                })}
              </div>
            </Field>
          </Section>

          {/* CADENCE */}
          <Section
            id="cadence"
            eyebrow={t('editStrategy.cadenceEyebrow')}
            title={t('editStrategy.cadenceTitle')}
            sub={t('editStrategy.cadenceSub')}
            locked={strategyLocked}
            lockedLabel={t('editStrategy.lockedBadge')}
          >
            <div className="es-cadence-wrap">
              {strategyLocked && (
                <div className="es-cadence-lock" role="note">
                  <span className="es-freeze-ic" aria-hidden>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>
                  </span>
                  <div>
                    <div className="es-freeze-t">{t('editStrategy.cadenceLockedTitle')}</div>
                    <p className="es-freeze-b">
                      {t('editStrategy.cadenceLockedBody')}{' '}
                      <button type="button" className="es-freeze-link" onClick={() => navigate('/app/plans')}>
                        {t('editStrategy.seePlans')}
                      </button>
                    </p>
                  </div>
                </div>
              )}
              <div className="es-cadence">
                <Field label={t('editStrategy.numberOfPostsLabel')}>
                  <div className="es-stepper">
                    <button
                      type="button"
                      disabled={strategyLocked}
                      onClick={() =>
                        update('cadence')({
                          ...data.cadence,
                          posts: Math.max(1, (data.cadence?.posts || 0) - 5),
                        })
                      }
                    >
                      −
                    </button>
                    <div className="es-stepper-v">
                      <span className="es-stepper-num">{data.cadence?.posts || 0}</span>
                      <span className="es-stepper-unit">{t('editStrategy.postsUnit')}</span>
                    </div>
                    <button
                      type="button"
                      disabled={strategyLocked}
                      onClick={() =>
                        update('cadence')({
                          ...data.cadence,
                          posts: Math.min(120, (data.cadence?.posts || 0) + 5),
                        })
                      }
                    >
                      +
                    </button>
                  </div>
                  <div className="es-stepper-presets">
                    {[7, 14, 30, 60].map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={data.cadence?.posts === n ? 'is-active' : ''}
                        disabled={strategyLocked}
                        onClick={() => update('cadence')({ ...data.cadence, posts: n })}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label={t('editStrategy.perLabel')}>
                  <div className="es-period">
                    {[
                      { v: 'week',    l: t('editStrategy.periodWeek') },
                      { v: 'month',   l: t('editStrategy.periodMonth') },
                      { v: 'quarter', l: t('editStrategy.periodQuarter') },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        className={`es-period-btn ${data.cadence?.period === o.v ? 'is-active' : ''}`}
                        disabled={strategyLocked}
                        onClick={() => update('cadence')({ ...data.cadence, period: o.v })}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                  <div className="es-cadence-calc">
                    ≈ <b>
                      {Math.max(1, Math.min(21, Math.round(
                        (data.cadence?.posts || 0) /
                          (PERIOD_TO_WEEKS[data.cadence?.period] || 4.3)
                      )))}
                    </b> {t('editStrategy.postsPerWeek')}
                  </div>
                  {hasPublishLimit && planName && (
                    <div className={`es-plan-cap ${publishLimitExceeded ? 'is-warning' : ''}`}>
                      {publishLimitExceeded ? (
                        <>
                          <span className="es-plan-cap-icon" aria-hidden>⚠</span>
                          <Trans
                            i18nKey="editStrategy.planPublishCapExceeded"
                            values={{ monthly: publishLimit }}
                            components={{ upgrade: <Link to="/app/plans" /> }}
                          />
                        </>
                      ) : t('editStrategy.planPublishCap', {
                        plan: planName,
                        monthly: publishLimit,
                        weekly: Math.floor(publishLimit / 4.3),
                      })}
                    </div>
                  )}
                </Field>
              </div>
            </div>
          </Section>

          {/* CHANNELS */}
          <Section
            id="channels"
            eyebrow={t('editStrategy.channelsEyebrow')}
            title={t('editStrategy.channelsTitle')}
            sub={t('editStrategy.channelsSub')}
          >
            <Field full>
              <div className="es-channels">
                {ES_PLATFORMS.map((p) => {
                  const platformAccounts = accounts.filter(
                    (a) => String(a.platform || '').toLowerCase() === p.id
                  );
                  const connected = connectedSet.has(p.id);
                  const on = connected && (data.channels || []).includes(p.id);
                  const sub = connected
                    ? platformAccounts
                        .map((a) => a.handle || a.accountName || a.displayName || a.username || t('editStrategy.accountFallback'))
                        .join(', ') || t('editStrategy.connected')
                    : t('editStrategy.notConnected');
                  return (
                    <button
                      key={p.id}
                      type="button"
                      className={`es-channel ${on ? 'is-on' : ''} ${!connected ? 'is-disabled' : ''}`}
                      onClick={() => connected && toggleIn('channels', p.id)}
                      disabled={!connected}
                    >
                      <span
                        className={`ob-net-ic-${p.id}`}
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        {BrandIcons[p.id]?.({ size: 15 })}
                      </span>
                      <div className="es-channel-text">
                        <div className="es-channel-name">{p.name}</div>
                        <div className="es-channel-sub">
                          {sub}
                          {platformAccounts.length > 1 && ` · ${t('editStrategy.accountCount', { count: platformAccounts.length })}`}
                        </div>
                      </div>
                      <span className={`es-channel-toggle ${on ? 'is-on' : ''}`}>
                        {on && <I.check size={11} />}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                className="es-channel-link"
                onClick={() => navigate('/app/settings?tab=accounts')}
                style={{ background: 'none', border: 0, cursor: 'pointer' }}
              >
                <I.settings size={12} /> {t('editStrategy.connectNewChannel')}
              </button>
            </Field>
          </Section>

          {error && (
            <div className="card" style={{ padding: 12, color: 'var(--danger-text)', border: '1px solid var(--danger)' }}>
              <I.alert size={14} /> {error}
            </div>
          )}

          {/* Spacer for sticky footer */}
          <div style={{ height: 24 }} />
        </div>
      </div>

      {/* Sticky save bar */}
      <div className={`es-save-bar ${isDirty ? 'is-dirty' : ''}`}>
        <div className="es-save-inner">
          <div className="es-save-text">
            {isDirty ? (
              <>
                <span className="dot pulse" style={{ background: 'var(--warning)' }} />
                <span>
                  {(!strategyLocked && materialChanged())
                    ? t('editStrategy.dirtyWithResearch')
                    : strategyRelevantChanged()
                      ? t('editStrategy.dirtyWithRecalc')
                      : t('editStrategy.dirtyBrandOnly')}
                </span>
              </>
            ) : (
              <>
                <I.check size={13} style={{ color: 'var(--success)' }} />
                <span>{t('editStrategy.upToDate')}</span>
              </>
            )}
          </div>
          <div className="es-save-actions">
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              disabled={!isDirty || saving}
              onClick={handleDiscard}
            >
              {t('editStrategy.discard')}
            </button>
            <button
              className="btn btn-primary btn-sm"
              type="button"
              disabled={!isDirty || saving || regenActive || !creationAllowed}
              onClick={onSaveClick}
            >
              {saving ? (
                <>
                  <I.refresh size={12} /> {t('editStrategy.saving')}
                </>
              ) : (
                <>
                  <I.check size={12} />{' '}
                  {(!strategyLocked && materialChanged())
                    ? t('editStrategy.saveAndResearch')
                    : strategyRelevantChanged() ? t('editStrategy.saveAndRecalculate') : t('editStrategy.saveChanges')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Paid brand-change confirm — re-runs market research + rebuilds the plan.
          Fully localized + built on the global .modal-scrim/.modal card so it
          looks native (the shared CreditConfirmationModal hardcodes English
          chrome, wrong for a UA/RU client). */}
      {confirmBrandOpen && (
        <div className="modal-scrim" role="presentation" onClick={() => !saving && setConfirmBrandOpen(false)}>
          <div className="modal card" role="dialog" aria-modal="true" aria-labelledby="es-bc-title" onClick={(e) => e.stopPropagation()}>
            <div className="type-icon"><I.globe size={18} /></div>
            <h3 id="es-bc-title" className="h3">{t('editStrategy.brandChangeTitle')}</h3>
            <p className="body-sm" style={{ color: 'var(--text-2)' }}>{t('editStrategy.brandChangeBody')}</p>
            <ol style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--text-2, var(--text-muted))', lineHeight: 1.65 }}>
              <li>{t('editStrategy.brandChangeStep1')}</li>
              <li>{t('editStrategy.brandChangeStep2')}</li>
            </ol>
            <div className="auth-note" style={{ marginTop: 12 }}>
              {usageLoading
                ? t('editStrategy.brandChangeUsageChecking')
                : researchUsage && researchUsage.limit != null
                  ? t('editStrategy.brandChangeUsage', { remaining: Math.max(0, researchUsage.remaining ?? 0), limit: researchUsage.limit })
                  : t('editStrategy.brandChangeUsageUnlimited')}
            </div>
            {!usageLoading && researchUsage && researchUsage.limit != null && (researchUsage.remaining ?? 0) < 1 && (
              <div className="auth-error" style={{ marginTop: 12 }}><I.alert size={13} /> {t('editStrategy.brandChangeNoQuota')}</div>
            )}
            <div className="row gap-8" style={{ marginTop: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleSave(true)}
                disabled={saving || usageLoading || !creationAllowed || (researchUsage && researchUsage.limit != null && (researchUsage.remaining ?? 0) < 1)}
              >
                {saving ? t('editStrategy.brandChangeConfirming') : t('editStrategy.brandChangeConfirm')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/app/settings?tab=billing')} disabled={saving}>
                {t('editStrategy.freezeCta')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setConfirmBrandOpen(false)} disabled={saving}>
                {t('editStrategy.brandChangeCancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditStrategy;
